// eslint-disable-next-line import/no-cycle

import type { SortingState } from '@tanstack/react-table';
import QueryString from 'qs';
import { z } from 'zod';

import authService from '@/services/auth/auth';

import { DjangoDataZodSchema, statusSchema } from './common';
import { apiRoutes } from './config';
import { externalOfferSchema } from './external-offers';
import { SeniorityLevelEnum } from './externalCandidates';

const baseOfferRecommendationsSchema = z.object({
  id: z.number(),
  status: statusSchema,
  seniorityLevel: z.nativeEnum(SeniorityLevelEnum),
  resume: z.object({
    id: z.number(),
    name: z.string(),
    jobTitle: z.string(),
    resumeName: z.string(),
  }),
  createdAt: z.string(),
});

const recommendationsSchema = z.object({
  id: z.number(),
  status: statusSchema,
  similarityScore: z.number(),
  screeningProbability: z.number(),
  gapScore: z.number(),
  missingElements: z.array(z.string()),
  quickWins: z.array(z.string()),
  isAchievable: z.boolean(),
  offer: externalOfferSchema,
});

export type OfferRecommendations = z.infer<typeof recommendationsSchema>;

export const getOfferRecommendationsList = async ({
  limit = 20,
  offset = 0,
}: {
  offset?: number;
  limit?: number;
}) => {
  const { data } = await authService.get(
    apiRoutes.resume.offerRecommendations._root,
    {
      params: {
        limit,
        offset,
      },
    }
  );

  return DjangoDataZodSchema(baseOfferRecommendationsSchema).parse(data);
};

export const getOfferRecommendations = async (
  id: string,
  order?: SortingState
) => {
  const { data } = await authService.get(
    apiRoutes.resume.offerRecommendations.detail(id).recommendations,
    {
      params: {
        limit: 100,
        offset: 0,
        order: order?.map(
          ({ desc, id: fieldId }) => `${desc ? '-' : ''}${fieldId}`
        ),
      },
      paramsSerializer: (params) =>
        QueryString.stringify(params, { arrayFormat: 'repeat' }),
    }
  );

  return DjangoDataZodSchema(recommendationsSchema)
    .extend({ status: statusSchema, resumeId: z.number() })
    .parse(data);
};

export const postOfferRecommendations = async ({
  resumeId,
  requestedOffersCount,
}: {
  resumeId: number;
  requestedOffersCount: number;
}) => {
  const { data } = await authService.post(
    apiRoutes.resume.offerRecommendations._root,
    {
      resume_id: resumeId,
      requested_offers_count: requestedOffersCount,
    }
  );

  return z.object({ batchId: z.number() }).parse(data);
};
